/**
 * <SelectField> — un champ qui ouvre sa liste de choix (molecule).
 *
 * Le sélecteur des formulaires du compte : le champ montre le choix en cours et un chevron, le
 * toucher ouvre une feuille (`<Modal>`) qui porte la liste (`<Autocomplete>`). Même patron que
 * `<DatePicker>` : un champ pressable et son sélecteur encapsulé, la valeur en props. Quelques
 * réponses courtes qu'on tape d'un geste prennent une rangée de pilules (`<ChoiceChip>`) ; ce
 * champ sert dans un formulaire long, quand les options sont nombreuses (un sport, un pays) ou
 * longues (une phrase), et qu'on veut lire la réponse en cours sans dérouler la liste.
 *
 * Choix unique : toucher une option la choisit et referme la feuille. Choix multiple : les choix
 * s'affichent en chips dans le champ, et la feuille se referme par « Terminer ». La recherche
 * n'apparaît qu'au-delà de quelques options ; `searchable` la force, et elle prend alors le curseur à
 * l'ouverture : on tape tout de suite.
 *
 * Une longue liste à chercher (les chapitres d'une matière) garde ce que `<Autocomplete>` sait de
 * plus, chaque fois absent par défaut : ses groupes (`groupBy`), l'issue d'une recherche sans
 * résultat (`noResultAction`, qui referme la feuille) et la forme des options qui arrivent
 * (`loading`).
 *
 * `presentation="inline"` en fait un champ d'autocomplétion : sa liste s'ouvre sous lui, dans la page, sans feuille
 * (`<SelectFieldInline>`), et on y tape pour chercher. Pour un formulaire déjà dans une boîte, ou au live, où une
 * feuille de plus couvrirait ce qu'on règle (« Générer une activité »). Choix unique. `getItemIcon` y pose l'icône
 * d'une option devant son libellé, dans la liste et dans le champ.
 *
 * L'ouverture de la feuille est un état d'interface, donc local ; `defaultOpen` l'ouvre d'office
 * pour une story.
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { TEXT_INPUT_HEIGHT } from "../../atoms/TextInput/TextInput"
import { ErrorText } from "../../atoms/typography/ErrorText"
import { Text } from "../../atoms/typography/Text"
import { Autocomplete } from "../Autocomplete"
import {
    type AutocompleteGroup,
    type AutocompleteItemIcon,
    type AutocompleteNoResultAction,
} from "../Autocomplete/Autocomplete"
import { Modal } from "../Modal"
import { SelectFieldInline } from "./SelectFieldInline"
import { type UISelectOption } from "../../../ports/primary/ui/types"

export type { UISelectOption }

/** En dessous, la liste se lit d'un coup d'œil : pas de champ de recherche. */
const SEARCH_FROM = 15

export type SelectFieldProps<T> = {
    label?: string
    /** Ce que le champ affiche tant que rien n'est choisi. Défaut « Choisir ». */
    placeholder?: string
    items: T[]
    getItemId: (item: T) => string | number
    getItemLabel: (item: T) => string
    /** Le choix en cours. Un seul id au plus en choix unique. */
    selectedIds: Array<string | number>
    /** La sélection après le geste : `[id]` en choix unique, la liste à jour en choix multiple. */
    onChange: (ids: Array<string | number>) => void
    /** Plusieurs choix à la fois. Défaut `false` ; sans effet en `inline`, qui ne fait qu'un choix. */
    multiple?: boolean
    /**
     * `"sheet"` (défaut) : le champ ouvre une feuille. `"inline"` : un champ d'autocomplétion, sa liste ancrée sous
     * lui, dans la page.
     */
    presentation?: "sheet" | "inline"
    /** L'icône d'une option devant son libellé, dans la liste et dans le champ (`inline`). Absente : pas d'icône. */
    getItemIcon?: (item: T) => AutocompleteItemIcon | null
    searchPlaceholder?: string
    /** Le champ de recherche de la feuille. Défaut : à partir de quinze options. */
    searchable?: boolean
    /** Les intitulés de groupe de la liste (`<Autocomplete groupBy>`). Absent : une liste à plat. */
    groupBy?: (item: T) => AutocompleteGroup | null
    /** Une recherche sans résultat offre cette issue, et la feuille se referme quand on la prend. */
    noResultAction?: AutocompleteNoResultAction
    /** Les options arrivent : la feuille en montre la forme. */
    loading?: boolean
    /** Message d'erreur sous le champ : le contour passe au rouge. */
    error?: string
    disabled?: boolean
    /** Ouvre la feuille au premier rendu (stories). */
    defaultOpen?: boolean
    testID?: string
}

export function SelectField<T>(props: SelectFieldProps<T>) {
    const {
        label,
        placeholder = "Choisir",
        items,
        getItemId,
        getItemLabel,
        selectedIds,
        onChange,
        multiple = false,
        searchPlaceholder,
        searchable,
        groupBy,
        noResultAction,
        loading = false,
        error,
        disabled = false,
        defaultOpen = false,
        presentation = "sheet",
        testID,
    } = props

    const [open, setOpen] = useState(defaultOpen)
    const withSearch = searchable ?? items.length >= SEARCH_FROM
    if (presentation === "inline") return <SelectFieldInline {...props} withSearch={withSearch} />

    const selectedKeys = new Set(selectedIds.map(String))
    const selectedItems = items.filter((item) => selectedKeys.has(String(getItemId(item))))
    const empty = selectedItems.length === 0
    const borderKey: SemanticColorKey = error != null && !disabled ? "danger" : "border"
    const summary = empty ? placeholder : selectedItems.map(getItemLabel).join(", ")

    const toggle = (id: string | number) => {
        if (!multiple) {
            onChange([id])
            setOpen(false)
            return
        }
        const key = String(id)
        onChange(selectedKeys.has(key) ? selectedIds.filter((x) => String(x) !== key) : [...selectedIds, id])
    }

    return (
        <Stack gap="1" testID={testID}>
            {label != null && (
                <Text size="text-base" weight="bold" color="primary">
                    {label}
                </Text>
            )}

            <Pressable
                disabled={disabled}
                onPress={() => setOpen(true)}
                accessibilityRole="button"
                accessibilityLabel={label != null ? `${label} : ${summary}` : summary}
                accessibilityState={{ disabled }}
                style={({ pressed }) => [styles.field(borderKey, disabled), pressed ? styles.pressed : undefined]}
            >
                <Row gap="2" align="center">
                    {multiple && !empty ? (
                        <View style={styles.chips}>
                            {selectedItems.map((item) => (
                                <Badge key={String(getItemId(item))} text={getItemLabel(item)} variant="primary" />
                            ))}
                        </View>
                    ) : (
                        <Text color={empty ? "text-muted" : "text-strong"} style={styles.value}>
                            {summary}
                        </Text>
                    )}
                    <Icon name="chevron-down" size={16} color="text-muted" />
                </Row>
            </Pressable>

            {error != null && <ErrorText live>{error}</ErrorText>}

            <Modal
                visible={open}
                onClose={() => setOpen(false)}
                // Une liste se referme d'un geste (le voile, Échap, le retour d'Android) sans rien perdre : le
                // choix part au toucher. Sans lui, Échap passait au calque du dessous et fermait « Plus d'options ».
                dismissable
                title={label ?? placeholder}
                // La tête d'une étape : un intitulé long passe à la ligne au lieu de buter sur la croix.
                steps={{ step: 1, total: 1 }}
                sizeToContent
                footer={multiple ? <Button label="Terminer" fullWidth onPress={() => setOpen(false)} /> : undefined}
            >
                <Autocomplete
                    selection={multiple ? "multiple" : "single"}
                    searchable={withSearch}
                    // Le curseur dans la recherche seulement quand l'appelant la demande : une longue liste
                    // qu'on parcourt (une heure, un pays) ne doit pas ouvrir le clavier par-dessus.
                    autoFocusSearch={searchable === true}
                    items={items}
                    getItemId={getItemId}
                    getItemLabel={getItemLabel}
                    selectedIds={selectedIds}
                    onToggle={toggle}
                    searchPlaceholder={searchPlaceholder}
                    groupBy={groupBy}
                    noResultAction={
                        noResultAction != null
                            ? {
                                  ...noResultAction,
                                  onPress: (query) => {
                                      noResultAction.onPress(query)
                                      setOpen(false)
                                  },
                              }
                            : undefined
                    }
                    loading={loading}
                />
            </Modal>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La boîte de `<TextInput>` : même contour, même rayon, même hauteur de ligne. */
    field: (borderKey: SemanticColorKey, disabled: boolean) => ({
        borderWidth: 1,
        borderColor: theme.colors[borderKey],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors[disabled ? "surface-subtle" : "surface-raised"],
        paddingHorizontal: theme.spacing["3"],
        paddingVertical: theme.spacing["2"],
        minHeight: TEXT_INPUT_HEIGHT,
        justifyContent: "center",
        opacity: disabled ? 0.5 : 1,
    }),
    pressed: { opacity: 0.6 },
    value: { flexShrink: 1, flexGrow: 1 },
    chips: {
        flexDirection: "row",
        flexWrap: "wrap",
        flexShrink: 1,
        flexGrow: 1,
        gap: theme.spacing["1"],
    },
}))
